<template>
  <div>
    <b-card :class="{ 'category-summary-card-with-hint': hintId !== null }">
      <hint-popup
        v-if="hintId !== null"
        :id="hintId"
        class="category-summary-hint-popup"
      />
      <h3 class="mb-2 category-summary-title">
        <slot name="title" />
      </h3>

      <slot name="description" />

      <loading-container
        v-if="loading"
      />
      <error-display
        v-else-if="error"
        @action-clicked="getCategorySummary"
      />
      <NoDataContainer
        v-else-if="!items || items.length < 1"
      />
      <div v-else>
        <div class="ecommerce-searchbar mb-2">
          <b-input-group class="input-group-merge">
            <b-form-input
              v-model="search"
              placeholder="Search..."
              class="search-product"
            />
            <b-input-group-append is-text>
              <feather-icon
                icon="SearchIcon"
                class="text-muted"
              />
            </b-input-group-append>
          </b-input-group>
        </div>

        <b-table
          responsive="sm"
          hover
          :items="sortedItems"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :per-page="perPage"
          :current-page="currentPage"
          @row-clicked="rowClicked"
        >
          <template #cell(name)="data">
            <div class="d-flex align-items-center">
              <!-- Provider logo -->
              <b-img
                v-if="category === 'provider' && data.item.id"
                :src="`${baseURL}serveproviderlogo.cfm?id=${data.item.id}`"
                alt="provider logo"
                class="category-icon mr-1"
              />
              <!-- Product type icon -->
              <b-avatar
                v-else-if="category === 'producttype' && data.item.pticonname"
                size="32"
                :variant="data.item.pticoncolor || 'light-primary'"
                rounded
                class="mr-1"
              >
                <feather-icon
                  :icon="data.item.pticonname"
                  size="16"
                />
              </b-avatar>
              <span>{{ data.item.name }}</span>
            </div>
          </template>
          <template v-if="category === 'provider'" #cell(avescore)="data">
            {{ formatScore(data.value) }}
          </template>
          <template v-if="category === 'provider'" #cell(maxscore)="data">
            {{ formatScore(data.value) }}
          </template>
        </b-table>

        <div class="d-flex p-2 justify-content-between align-items-center">
          <div>
            <span class="pb-1 d-block">Rows Per Page</span>
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              class="pagination-select"
            />
          </div>
          <div class="d-flex flex-column align-items-end">
            <span class="mb-1">{{ sortedItems.length }} results found | Showing results {{ showingResults }}</span>
            <b-pagination
              v-model="currentPage"
              :total-rows="sortedItems.length"
              :per-page="perPage"
              align="center"
              size="sm"
              class="my-0"
            />
          </div>
        </div>
      </div>
    </b-card>

    <!-- Previous Providers (Collapsible) - Only for provider category -->
    <b-card
      v-if="category === 'provider' && excludedContent.length > 0"
      no-body
      class="excluded-providers-collapse mt-2"
    >
      <hint-popup
        :id="10"
        class="excluded-providers-hint-popup"
      />
      <custom-collapsable
        title="Previously Active Providers"
        :is-visible="false"
      >
        <div class="providers-grid p-2">
          <div
            v-for="provider in excludedContent"
            :key="provider.id"
            class="provider-card cursor-pointer"
            @click="rowClicked(provider)"
          >
            <h6 class="mb-0">
              {{ provider.name }}
            </h6>
            <p class="mb-0 small text-muted">
              Matured products: {{ provider.count }}
            </p>
          </div>
        </div>
      </custom-collapsable>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BTable, BImg, BAvatar, BPagination, BInputGroup, BInputGroupAppend, BFormInput, BFormSelect,
} from 'bootstrap-vue'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import ErrorDisplay from '@/views/components/ErrorDisplay.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import CustomCollapsable from '@/views/components/CustomCollapsable.vue'
import HintPopup from '@/views/components/HintPopup.vue'

export default {
  components: {
    NoDataContainer,
    ErrorDisplay,
    LoadingContainer,
    BCard,
    BTable,
    BImg,
    BAvatar,
    BPagination,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BFormSelect,
    CustomCollapsable,
    HintPopup,
  },
  props: {
    hintId: {
      type: [String, Number],
      default: null,
    },
    category: {
      type: String,
      required: true,
      validator: value => ['provider', 'underlying', 'counterparty', 'producttype'].includes(value),
    },
    routeName: {
      type: String,
      required: true,
    },
    paramName: {
      type: String,
      required: true,
    },
    nameLabel: {
      type: String,
      default: 'Name',
    },
    priorityNames: {
      type: Array,
      default: () => [],
    },
    excludeNames: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      items: [],
      excludedContent: [],
      fields: [],
      sortBy: this.category === 'provider' ? 'avescore' : 'name',
      sortDesc: this.category === 'provider',

      perPage: 20,
      perPageOptions: [5, 10, 20, 50, 100],
      currentPage: 1,
      search: '',

      loading: false,
      error: false,
    }
  },
  watch: {
    search() {
      this.currentPage = 1
    },
    perPage() {
      this.currentPage = 1
    },
  },
  computed: {
    sortedItems() {
      let filteredItems = this.items

      // Exclude items if excludeNames is provided
      if (this.excludeNames.length > 0) {
        filteredItems = this.items.filter(item => !this.excludeNames.includes(item.name))
      }

      // Filter by search term
      if (this.search.trim()) {
        const term = this.search.trim().toLowerCase()
        filteredItems = filteredItems.filter(item => item.name && item.name.toLowerCase().includes(term))
      }

      // Apply priority sorting if priorityNames is provided
      if (this.priorityNames.length === 0) {
        return filteredItems
      }

      // Separate priority items and other items
      const priorityItems = []
      const otherItems = []

      filteredItems.forEach(item => {
        if (this.priorityNames.includes(item.name)) {
          priorityItems.push(item)
        } else {
          otherItems.push(item)
        }
      })

      // Sort priority items by their order in priorityNames array
      priorityItems.sort((a, b) => {
        const indexA = this.priorityNames.indexOf(a.name)
        const indexB = this.priorityNames.indexOf(b.name)
        return indexA - indexB
      })

      // Return priority items first, then other items
      return [...priorityItems, ...otherItems]
    },
    showingResults() {
      const from = (this.currentPage - 1) * this.perPage + 1
      let to = this.currentPage * this.perPage
      to = to > this.sortedItems.length ? this.sortedItems.length : to
      return `from ${from} to ${to}`
    },
  },
  mounted() {
    this.getCategorySummary()
  },
  methods: {
    getCategorySummary() {
      this.loading = true
      this.error = false

      this.$http
        .get(`public.cfc?method=getCategorySummary&category=${this.category}`)
        .then(response => {
          console.log(`Category Summary for ${this.category}:`, response.data)
          this.items = response.data.CONTENT

          // Store excluded content for provider category
          if (this.category === 'provider' && response.data.EXCLUDEDCONTENT) {
            this.excludedContent = response.data.EXCLUDEDCONTENT
          }

          if (response.data.HEADER && response.data.HEADER.length > 0) {
            const columnOrder = ['name', 'avescore', 'maxscore', 'averisk', 'countlast6m', 'count', 'active', 'current']

            // provider table has smaller name column, others have larger
            const nameColumnWidth = this.category === 'provider' ? '30%' : '50%'

            this.fields = response.data.HEADER
              .filter(header => header.key !== 'link')
              .sort((a, b) => {
                const indexA = columnOrder.indexOf(a.key)
                const indexB = columnOrder.indexOf(b.key)
                // If not in columnOrder, put at end
                if (indexA === -1) return 1
                if (indexB === -1) return -1
                return indexA - indexB
              })
              .map(header => ({
                key: header.key,
                label: header.label,
                sortable: true,
                thStyle: { width: header.key === 'name' ? nameColumnWidth : 'auto' },
              }))
          }
        })
        .catch(error => {
          console.error('Error fetching category summary:', error)
          this.error = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    rowClicked(row) {
      this.$router.push({
        name: this.routeName,
        params: {
          [this.paramName]: row.id,
        },
      })
    },
    formatScore(value) {
      if (value === null || value === undefined || value === '') return '-'
      const num = parseFloat(value)
      if (Number.isNaN(num)) return value
      return num.toFixed(2)
    },
  },
}
</script>

<style scoped>
.category-summary-card-with-hint ::v-deep .card-body {
  position: relative;
}

.category-summary-card-with-hint .category-summary-title {
  padding-right: 2.5rem;
}

.category-summary-hint-popup {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}

.excluded-providers-collapse {
  position: relative;
}

.excluded-providers-collapse ::v-deep .card-header {
  padding-right: 4rem;
}

.excluded-providers-hint-popup {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}

.category-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.pagination-select {
  max-width: 200px;
}

.providers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.provider-card {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.provider-card:hover {
  border-color: #1a73e8;
  box-shadow: 0 2px 8px rgba(26, 115, 232, 0.15);
}

/* Dark Mode Support */
.dark-layout .provider-card {
  border-color: #3b4253;
  background-color: #283046;
}

.dark-layout .provider-card:hover {
  border-color: #1a73e8;
}

/* Responsive */
@media (max-width: 767px) {
  .providers-grid {
    grid-template-columns: 1fr;
  }
}
</style>
